import type { ReactNode } from 'react';
import MarkdownRenderer from '@/components/common/MarkdownRenderer';
import { getModuleIcon } from '@/utils/moduleIcons';

interface SectionProps {
    title?: string;
    subtitle?: string;
    children: ReactNode;
    className?: string;
    accent?: 'purple' | 'blue' | 'green' | 'muted' | 'none' | 'teal';
    iconKey?: string;
    iconClassName?: string;
    showBorder?: boolean;
}

const accentMap: Record<string, string> = {
    purple: 'bg-purple-100 border-purple-200',
    blue: 'bg-blue-100 border-blue-200',
    green: 'bg-emerald-100 border-emerald-200',
    teal: 'bg-teal-100 border-teal-200',
    muted: 'bg-gray-50 border-gray-200',
    none: 'bg-white border-gray-200',
};

const accentIconColor: Record<string, string> = {
    purple: 'text-purple-600',
    blue: 'text-blue-600',
    green: 'text-emerald-600',
    teal: 'text-teal-600',
    muted: 'text-gray-600',
    none: 'text-gray-600',
};

export default function Section({
    title,
    subtitle,
    children,
    className = '',
    accent = 'none',
    iconKey,
    iconClassName = '',
    showBorder = true,
}: SectionProps) {
    const IconComp = iconKey ? getModuleIcon(iconKey) : null;
    const iconColor = accentIconColor[accent];

    return (
        <section className={`rounded-lg ${showBorder ? 'p-5' : ''} ${accentMap[accent]} ${className}`}>
            {(title || subtitle) && (
                <header>
                    <div className="mb-4 flex flex-col">
                        <div className={`mb-2 ${IconComp ? 'flex items-center gap-3' : ''}`}>
                            {IconComp && (
                                <IconComp
                                    className={`h-5 w-5 shrink-0 ${iconColor} ${iconClassName}`}
                                    strokeWidth={2}
                                    aria-hidden="true"
                                />
                            )}
                            {title && <h3 className="text-lg leading-none font-semibold text-gray-900">{title}</h3>}
                        </div>

                        {subtitle && (
                            <div className="mt-1">
                                <MarkdownRenderer
                                    content={subtitle}
                                    variant="inline"
                                    className="text-base leading-snug text-gray-500"
                                />
                            </div>
                        )}
                    </div>
                </header>
            )}
            {children}
        </section>
    );
}
